import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AccountingFunctionsService,
  type IAccountCreditTransactionProvider,
  type IAppointmentCreditState,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  AccountCredit,
  Invoice,
  Patient,
  Transaction,
  getBaseTransaction,
  refundUsedCredits,
} from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  IAccountCredit,
  IAccountCreditExtendedData,
  IInvoice,
  ITransaction,
  InvoiceStatus,
  PatientRelationshipType,
  TransactionAction,
  TransactionProvider,
  type IAppointment,
} from '@principle-theorem/principle-core/interfaces';
import {
  Region,
  snapshot,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  RefundCreditTransactionDialogComponent,
  type IRefundCreditTransactionDialogData,
  type IRefundCreditTransactionDialogResult,
} from '../transaction-components/refund-credit-transaction-dialog/refund-credit-transaction-dialog.component';
import { RefundDepositPromptService } from '../transaction-components/refund-deposit-prompt/refund-deposit-prompt.service';
import {
  TransactionProviderType,
  type ITransactionProvider,
} from '../transaction-provider';
import { AccountCreditTransactionBuilder } from './account-credit-transaction-builder';
import {
  AccountCreditTransactionComponent,
  type IAccountCreditTransactionDialogData,
  type IAccountCreditTransactionReturnData,
} from './account-credit-transaction.component';
import {
  AppointmentCreditCapture,
  type AccountCreditTransactionRef,
} from './appointment-credit-capture';

@Injectable()
export class AccountCreditTransactionProvider
  implements ITransactionProvider, IAccountCreditTransactionProvider
{
  providerId = TransactionProvider.AccountCredit;
  providerType = TransactionProviderType.Payment;
  providerRegions = [Region.Australia, Region.NewZealand];
  isEnabled$ = of(true);

  constructor(
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _refundDepositPrompt: RefundDepositPromptService,
    private _accountFunctions: AccountingFunctionsService
  ) {}

  getInfo$(invoice: WithRef<IInvoice>): Observable<string> {
    return AppointmentCreditCapture.totalAvailableCredit$(
      this.getOwnerCredits$(invoice)
    ).pipe(map((credit) => `$${credit.toFixed(2)} available`));
  }

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    return AppointmentCreditCapture.totalAvailableCredit$(
      this.getOwnerCredits$(invoice)
    ).pipe(
      map(
        (creditAvailable) =>
          creditAvailable > 0 &&
          invoice.status !== InvoiceStatus.Paid &&
          Invoice.canAddTransactions(invoice)
      )
    );
  }

  async capture(
    invoice: WithRef<IInvoice>
  ): Promise<AccountCreditTransactionRef | undefined> {
    const credits = await snapshot(this.getOwnerCredits$(invoice));
    const appointment = await Invoice.getAssociatedAppointment(invoice);
    const result = await this._getTransaction(
      invoice,
      credits,
      appointment?.ref
    );
    if (!result) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }
    return this._accountFunctions.addTransactionToInvoice(
      invoice,
      result.transaction,
      TransactionAction.Add
    );
  }

  getOwnerCredits$(
    invoice: WithRef<IInvoice>
  ): Observable<WithRef<IAccountCredit>[]> {
    return Invoice.patient$(invoice).pipe(
      switchMap((patient) =>
        Patient.withPatientRelationships$(
          patient,
          [PatientRelationshipType.DuplicatePatient],
          AccountCredit.all$
        )
      )
    );
  }

  getAppointmentCreditState$(
    invoice: WithRef<IInvoice>,
    appointmentRef?: DocumentReference<IAppointment>
  ): Observable<IAppointmentCreditState> {
    return AppointmentCreditCapture.appointmentCreditState$(
      this.getOwnerCredits$(invoice),
      appointmentRef
    );
  }

  async autoApplyAppointmentCredits(
    invoice: WithRef<IInvoice>,
    appointmentRef: DocumentReference<IAppointment>
  ): Promise<AccountCreditTransactionRef | undefined> {
    try {
      const transaction =
        await AppointmentCreditCapture.buildAppointmentCreditCapture({
          invoice,
          appointmentRef,
          credits$: this.getOwnerCredits$(invoice),
        });
      if (!transaction) {
        return;
      }
      return await this._accountFunctions.addTransactionToInvoice(
        invoice,
        transaction,
        TransactionAction.Add
      );
    } catch (error) {
      this._snackbar.open('Failed to apply booking deposit');
      throw error;
    }
  }

  async refundTransaction(
    invoice: WithRef<IInvoice>,
    transaction: WithRef<ITransaction<IAccountCreditExtendedData>>,
    fromCredit?: WithRef<IAccountCredit>
  ): Promise<AccountCreditTransactionRef | undefined> {
    const acceptedRefundAlert =
      await this._refundDepositPrompt.showRefundDepositAlert(invoice);
    if (!acceptedRefundAlert) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }
    const credits = await Transaction.getAssociatedCredits(transaction);
    const result = await this._dialog
      .open<
        RefundCreditTransactionDialogComponent,
        IRefundCreditTransactionDialogData,
        IRefundCreditTransactionDialogResult
      >(
        RefundCreditTransactionDialogComponent,
        DialogPresets.medium({
          data: {
            invoice,
            credits,
            transaction,
            fromCredit,
          },
        })
      )
      .afterClosed()
      .toPromise();

    if (!result || result.amount <= 0) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }

    const patient = await snapshot(Invoice.patient$(invoice));

    const accountCreditsToRefund = refundUsedCredits(
      result.usedCredits,
      result.amount
    );

    const creditTransaction =
      AccountCreditTransactionBuilder.buildRefundCreditTransaction({
        transaction,
        amount: result.amount,
        accountCreditsUsed: result.usedCredits,
        accountCreditsToRefund,
      });

    const transactionRef = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      creditTransaction,
      TransactionAction.Refund
    );

    if (accountCreditsToRefund.remaining > 0) {
      const credit = AccountCredit.init({
        type: AccountCreditType.Refund,
        amount: result.amount,
        description: 'Refund',
        practiceRef: transaction.practiceRef,
      });

      await Invoice.addAccountCredit(patient, credit);
    }
    return transactionRef;
  }

  private async _getTransaction(
    invoice: WithRef<IInvoice>,
    credits: WithRef<IAccountCredit>[],
    appointmentRef?: DocumentReference<IAppointment>
  ): Promise<IAccountCreditTransactionReturnData | undefined> {
    const transaction = await getBaseTransaction(invoice);
    return this._dialog
      .open<
        AccountCreditTransactionComponent,
        IAccountCreditTransactionDialogData,
        IAccountCreditTransactionReturnData
      >(
        AccountCreditTransactionComponent,
        DialogPresets.medium<IAccountCreditTransactionDialogData>({
          data: { transaction, credits, invoice, appointmentRef },
        })
      )
      .afterClosed()
      .toPromise();
  }
}
